import {
  displaySpeakerName,
  type GtDoc,
  normalizeSpeakerName,
  roomsById,
  roomVoiceOrdinals,
  slugifySpeakerName,
  SPEAKER_HOTKEYS,
} from './gtModel'

import type { FinalizeBlockerReason, GtSpeaker, MeetingAttributionGtSnapshotTurn } from '@/api/internal'
import type { SpeakerIdentity, SpeakerMapping } from '@/pages/app/AttributionViewer/artifactDerivations'

/**
 * Speaker lookup by their assigned hotkey from SPEAKER_HOTKEYS (digits then letters); first speaker claiming a
 * key wins, and the map iterates in SPEAKER_HOTKEYS order so ActionBar buttons read 1..9, Q, W, …
 */
export const hotkeySpeakerMap = (speakers: readonly GtSpeaker[]): Map<string, GtSpeaker> => {
  const claimed = new Map<string, GtSpeaker>()
  for (const speaker of speakers) {
    if (speaker.hotkey && SPEAKER_HOTKEYS.includes(speaker.hotkey) && !claimed.has(speaker.hotkey)) {
      claimed.set(speaker.hotkey, speaker)
    }
  }
  return new Map(
    SPEAKER_HOTKEYS.flatMap((hotkey) => {
      const speaker = claimed.get(hotkey)
      return speaker ? ([[hotkey, speaker]] as const) : []
    })
  )
}

export const speakersById = (speakers: readonly GtSpeaker[]): Map<string, GtSpeaker> =>
  new Map(speakers.map((sp) => [sp.id, sp]))

export const liveTurns = (doc: GtDoc): GtDoc['turns'] => doc.turns.filter((t) => t.state !== 'deleted')

/** Live turns assigned to any of the given speakers. */
export const liveTurnsOwnedBy = (doc: GtDoc, speakerIds: readonly string[]): number => {
  const wanted = new Set(speakerIds)
  return liveTurns(doc).filter((turn) => turn.speaker_id !== null && wanted.has(turn.speaker_id)).length
}

export const snapshotByTurnId = (
  turnsSnapshot: readonly MeetingAttributionGtSnapshotTurn[]
): Record<string, MeetingAttributionGtSnapshotTurn> => {
  const map: Record<string, MeetingAttributionGtSnapshotTurn> = {}
  for (const turn of turnsSnapshot) map[turn.turn_id] = turn
  return map
}

export interface GtProgress {
  turnCount: number
  reviewedCount: number
  status: 'complete' | 'in_progress'
}

/** turn_count / reviewed_count mirror the server's live (non-deleted) / confirmed-only wire semantics. */
export const deriveProgress = (doc: GtDoc): GtProgress => {
  const live = liveTurns(doc)
  const turnCount = live.length
  const reviewedCount = live.filter((t) => t.state === 'confirmed').length
  const status = turnCount > 0 && live.every((t) => t.state !== null) ? 'complete' : 'in_progress'
  return { turnCount, reviewedCount, status }
}

export const turnAtTime = (
  orderedTurns: readonly MeetingAttributionGtSnapshotTurn[],
  ms: number
): MeetingAttributionGtSnapshotTurn | null => {
  let match: MeetingAttributionGtSnapshotTurn | null = null
  for (const turn of orderedTurns) {
    if (turn.start_ms > ms) break
    match = turn
  }
  return match ?? orderedTurns[0] ?? null
}

export const fullTranscriptOrder = (
  turns: readonly MeetingAttributionGtSnapshotTurn[]
): MeetingAttributionGtSnapshotTurn[] => [...turns].sort((a, b) => a.start_ms - b.start_ms)

const canonicalStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalStringify).join(',')}]`
  const record = value as Record<string, unknown>
  const keys = Object.keys(record).sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalStringify(record[key])}`).join(',')}}`
}

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')

/** Default fields are omitted from the hash, so legacy documents retain their concurrency token. */
const speakerHashView = (speaker: GtSpeaker): Partial<GtSpeaker> => {
  const view: Partial<GtSpeaker> = { ...speaker }
  if (view.voice_name == null) delete view.voice_name
  if (speaker.kind !== 'person' || speaker.room_id !== null) return view
  delete view.kind
  delete view.room_id
  return view
}

/**
 * Must stay byte-compatible with aria-web's `_canonical_json_sha256`: `{speakers, turns}` plus `rooms` only when
 * non-empty, so a document saved before rooms existed and the same document with defaults filled in hash alike.
 */
export const canonicalJsonSha256 = async (doc: GtDoc): Promise<string> => {
  const speakers = doc.speakers.map(speakerHashView)
  const json = canonicalStringify(
    doc.rooms.length > 0 ? { speakers, turns: doc.turns, rooms: doc.rooms } : { speakers, turns: doc.turns }
  )
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(json))
  return toHex(digest)
}

export { slugifySpeakerName }

/** Stable per-speaker color key; keyed by the document-local speaker id, which survives renames. */
export const rosterColorKey = (speakerId: string): string => `gt:${speakerId}`

const namesMatch = (a: string, b: string): boolean => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()

// Reuses a matching speakerMappings entry's colorKey by name so the shared color map stays consistent.
export const toSpeakerIdentity = (speaker: GtSpeaker, speakerMappings?: SpeakerMapping[]): SpeakerIdentity => {
  const reused = speakerMappings?.find((mapping) => mapping.name && namesMatch(mapping.name, speaker.name))
  return {
    key: speaker.id,
    colorKey: reused ? reused.colorKey : rosterColorKey(speaker.id),
    label: speaker.name,
    name: speaker.name,
  }
}

export interface GtFinalizeBlocker {
  reason: FinalizeBlockerReason
  turnId: string | null
  speakerId: string | null
}

const GENERIC_SPEAKER_RE = /^speaker \d+$/

/** Readiness blockers for finalize; identical rule to the server's finalize_blockers over live turns/speakers. */
export const deriveFinalizeBlockers = (doc: GtDoc): GtFinalizeBlocker[] => {
  const live = liveTurns(doc)
  if (live.length === 0) return [{ reason: 'no_turns', turnId: null, speakerId: null }]

  const blockers: GtFinalizeBlocker[] = []
  const firstTurnForSpeaker = new Map<string, string>()
  for (const turn of live) {
    if (!turn.speaker_id) blockers.push({ reason: 'no_speaker', turnId: turn.id, speakerId: null })
    else if (turn.state === null) blockers.push({ reason: 'undecided', turnId: turn.id, speakerId: null })
    if (turn.speaker_id && !firstTurnForSpeaker.has(turn.speaker_id)) firstTurnForSpeaker.set(turn.speaker_id, turn.id)
  }

  const speakerById = new Map(doc.speakers.map((sp) => [sp.id, sp]))
  const rooms = roomsById(doc)
  const ordinals = roomVoiceOrdinals(doc)
  const referenced = Array.from(firstTurnForSpeaker).flatMap(([speakerId, turnId]) => {
    const speaker = speakerById.get(speakerId)
    return speaker ? [{ speaker, turnId }] : []
  })

  // Room keys share the export namespace with speaker keys: a room and a speaker (or two rooms) that slug
  // alike would be merged by the export, unless the speaker is that room's only voice.
  const roomKeyOwners = new Map<string, string>()
  for (const { speaker, turnId } of referenced) {
    const room = speaker.room_id === null ? undefined : rooms.get(speaker.room_id)
    if (!room) continue
    const roomKey = slugifySpeakerName(room.label)
    if (!roomKey) {
      blockers.push({ reason: 'empty_key', turnId, speakerId: speaker.id })
      continue
    }
    const owner = roomKeyOwners.get(roomKey)
    if (owner && owner !== room.id) blockers.push({ reason: 'key_collision', turnId, speakerId: speaker.id })
    else roomKeyOwners.set(roomKey, room.id)
  }

  const keyOwners = new Map<string, string>()
  for (const { speaker, turnId } of referenced) {
    const speakerId = speaker.id
    // Checked on the exported name: a room voice's "<room> · voice N" is never generic, a lone voice's bare
    // room label may be.
    const displayName = displaySpeakerName(speaker, rooms, ordinals)
    if (GENERIC_SPEAKER_RE.test(normalizeSpeakerName(displayName))) {
      blockers.push({ reason: 'generic_speaker', turnId, speakerId })
    }
    const key = slugifySpeakerName(displayName)
    if (!key) {
      blockers.push({ reason: 'empty_key', turnId, speakerId })
      continue
    }
    const owner = keyOwners.get(key)
    const roomOwner = roomKeyOwners.get(key)
    if ((owner && owner !== speakerId) || (roomOwner !== undefined && roomOwner !== speaker.room_id)) {
      blockers.push({ reason: 'key_collision', turnId, speakerId })
    } else keyOwners.set(key, speakerId)
  }
  return blockers
}

/** Live turns nobody has confirmed yet. */
export const deriveUnreviewedTurnIds = (doc: GtDoc): Set<string> =>
  new Set(liveTurns(doc).flatMap((turn) => (turn.state === null ? [turn.id] : [])))

/** Live turns a reviewer must still act on before finalize: speakerless, or owned by a blocked speaker. */
export const deriveAttentionTurnIds = (doc: GtDoc, blockers: GtFinalizeBlocker[]): Set<string> => {
  const blockedSpeakers = new Set(blockers.flatMap((blocker) => (blocker.speakerId ? [blocker.speakerId] : [])))
  const ids = new Set<string>()
  for (const turn of liveTurns(doc)) {
    if (!turn.speaker_id || blockedSpeakers.has(turn.speaker_id)) ids.add(turn.id)
  }
  return ids
}
